import debounce from 'lodash-es/debounce';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { SocketContext } from '@/contexts/socket';
import { sessionIdSelector } from '@/state/session';
import { SocketEvent } from '@/ts/socket';
const DEBOUNCE_MS = 100;
export const useFileDownload = () => {
    const socket = useContext(SocketContext);
    const session_id = useRecoilValue(sessionIdSelector);
    // Track requested file_ids to prevent duplicates after emission
    const requestedFilesRef = useRef(new Set());
    // Debounced emitter per file_id
    const debouncedEmittersRef = useRef(new Map());
    useEffect(() => {
        if (!socket) {
            return;
        }
        const requestedFiles = requestedFilesRef.current;
        const debouncedEmitters = debouncedEmittersRef.current;
        const handleResponse = (data) => {
            requestedFiles.delete(data.file_id);
            const emitter = debouncedEmitters.get(data.file_id);
            if (emitter) {
                emitter.cancel();
                debouncedEmitters.delete(data.file_id);
            }
        };
        socket.on(SocketEvent.session_files_download_request_success, handleResponse);
        socket.on(SocketEvent.session_files_download_request_error, handleResponse);
        return () => {
            socket.off(SocketEvent.session_files_download_request_success, handleResponse);
            socket.off(SocketEvent.session_files_download_request_error, handleResponse);
            // Cancel pending debounced emitters when socket changes
            debouncedEmitters.forEach((emitter) => emitter.cancel());
            debouncedEmitters.clear();
            requestedFiles.clear();
        };
    }, [socket]);
    const handleFileDownload = useCallback((file_id, file_name, original_name) => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || requestedFilesRef.current.has(file_id)) {
            return;
        }
        const debouncedEmitters = debouncedEmittersRef.current;
        let emitter = debouncedEmitters.get(file_id);
        if (!emitter) {
            emitter = debounce((data) => {
                if (socket === null || socket === void 0 ? void 0 : socket.connected) {
                    requestedFilesRef.current.add(data.file_id);
                    socket.emit(SocketEvent.session_files_download_request, data);
                }
            }, DEBOUNCE_MS);
            debouncedEmitters.set(file_id, emitter);
        }
        emitter({
            file_id,
            file_name,
            session_id,
            original_name,
        });
    }, [session_id, socket]);
    return { handleFileDownload };
};
